<template>
  <div class="rounded-2xl border border-amber-300/60 dark:border-amber-500/30 bg-amber-50/70 dark:bg-amber-500/10 p-4">
    <div class="flex flex-wrap items-center gap-3">
      <span class="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400">
        <EyeIcon class="h-3.5 w-3.5" />
        Internal view
      </span>
      <p v-if="selected" class="text-sm text-surface-700 dark:text-surface-200">
        Viewing as <span class="font-semibold">{{ selected.customer_name }}</span>
        <span class="text-surface-500 dark:text-surface-400"> — {{ selected.site }} (#{{ selected.customer_no }})</span>
      </p>
      <p v-else class="text-sm text-surface-600 dark:text-surface-300">
        Select a customer to view their portal.
      </p>
      <button
        v-if="switchable"
        @click="toggleSearch"
        class="ml-auto rounded-xl px-3 py-1.5 text-sm font-medium text-amber-700 dark:text-amber-400 hover:bg-amber-500/10 transition-colors"
      >
        {{ searchOpen ? 'Close' : (selected ? 'Switch customer' : 'Choose customer') }}
      </button>
    </div>

    <div v-if="switchable && searchOpen" class="relative mt-3">
      <input
        v-model="query"
        type="text"
        placeholder="Search by customer name or number…"
        class="w-full rounded-xl border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-800 px-4 py-2.5 text-sm text-surface-800 dark:text-surface-100 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-amber-500/40"
        @focus="ensureResults"
      />
      <div
        class="absolute z-30 mt-1 max-h-72 w-full overflow-y-auto rounded-xl border border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-800 shadow-lg"
      >
        <p v-if="loading" class="px-4 py-3 text-sm text-surface-500">Searching…</p>
        <p v-else-if="loadError" class="px-4 py-3 text-sm text-red-500">{{ loadError }}</p>
        <p v-else-if="results.length === 0 && hasSearched" class="px-4 py-3 text-sm text-surface-500">No matching customers.</p>
        <button
          v-for="entry in results"
          :key="entry.mcs_customer_id"
          @click="select(entry)"
          class="block w-full px-4 py-2.5 text-left text-sm hover:bg-surface-100 dark:hover:bg-surface-700 transition-colors"
        >
          <span class="font-medium text-surface-800 dark:text-surface-100">{{ entry.customer_name }}</span>
          <span class="text-surface-500 dark:text-surface-400"> — {{ entry.site }} (#{{ entry.customer_no }})</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onBeforeUnmount, defineProps, defineEmits } from 'vue'
import { useStore } from 'vuex'
import { EyeIcon } from '@heroicons/vue/24/outline'
import { authFetch } from '@/composables/useApi'

const props = defineProps({
  switchable: { type: Boolean, default: true },
})
const emit = defineEmits(['selected'])

const store = useStore()
const apiUrl = store.state.apiUrl

const selected = computed(() => store.state.portalAdminCustomer)
const searchOpen = ref(!store.state.portalAdminCustomer)
const query = ref('')
const results = ref([])
const loading = ref(false)
const loadError = ref(null)
// Don't show "No matching customers." until at least one search has completed.
const hasSearched = ref(false)
let debounceTimer = null
// Sequence guard: only the newest in-flight request may write results/loading,
// so a stale slow response can never overwrite newer results.
let requestSeq = 0
// Set when we programmatically clear the query (on select) so the query
// watcher doesn't schedule a pointless trailing debounce fetch.
let suppressNextQueryWatch = false

const fetchResults = async () => {
  const seq = ++requestSeq
  loading.value = true
  loadError.value = null
  try {
    const url = `${apiUrl}api/v1/portal-admin/mcs-customers?q=${encodeURIComponent(query.value)}&limit=20`
    const resp = await authFetch(url, {}, store.state.user)
    if (seq !== requestSeq) return
    if (!resp.ok) throw new Error(`Search failed (${resp.status})`)
    const body = await resp.json()
    if (seq !== requestSeq) return
    results.value = body.data || []
    hasSearched.value = true
  } catch (e) {
    if (seq !== requestSeq) return
    loadError.value = e.message || 'Search failed'
    results.value = []
  } finally {
    if (seq === requestSeq) loading.value = false
  }
}

watch(query, () => {
  if (suppressNextQueryWatch) {
    suppressNextQueryWatch = false
    return
  }
  clearTimeout(debounceTimer)
  debounceTimer = setTimeout(fetchResults, 300)
})

const ensureResults = () => {
  if (!results.value.length && !loading.value) fetchResults()
}

const toggleSearch = () => {
  searchOpen.value = !searchOpen.value
  if (searchOpen.value) ensureResults()
}

// The search panel starts open when no customer is selected yet — kick off the
// initial list so the dropdown shows "Searching…" instead of an empty box.
if (searchOpen.value) ensureResults()

const select = (entry) => {
  store.commit('setPortalAdminCustomer', entry)
  emit('selected', entry)
  searchOpen.value = false
  // Cancel any pending debounce and invalidate any in-flight request, then
  // clear the query without triggering another fetch via the watcher.
  clearTimeout(debounceTimer)
  requestSeq++
  loading.value = false
  if (query.value !== '') {
    suppressNextQueryWatch = true
    query.value = ''
    // Drop query-filtered results so the next open fetches the full list.
    results.value = []
  }
}

onBeforeUnmount(() => clearTimeout(debounceTimer))
</script>
